import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { parseString as parseXmlString } from 'xml2js';
import { Select, Store } from '@ngxs/store';
import { combineLatest, Observable, withLatestFrom, map } from 'rxjs';
import {
  ADDRESS_TYPE_OPTIONS,
  BILLING_ADDRESS_TYPE,
  CORRESPONDENCE_ADDRESS_TYPE,
  SHIPPING_ADDRESS_TYPE,
} from 'src/app/core/constants';
import {
  Address,
  mapAddress,
  mapPatientAddressFormValues,
  PatientAddress,
  PatientAddressFormValues,
  UsState,
} from 'src/app/core/models';
import { AddressVerificationService } from 'src/app/core/services/address-verification.service';
import { validateAllFormFields, zipCodeValidator } from 'src/app/core/utils/validators';
import { LookupState } from 'src/app/core/store/lookup/lookup.state';
import { omit } from 'lodash-es';
import { CreatePatientAddress } from 'src/app/core/store/addresses/addresses.actions';
import { AddressesState } from 'src/app/core/store/addresses/addresses.state';
import { ApiStoreData } from 'src/app/core/store/utils';
import { asFormControl } from 'src/app/core/utils/common';

interface VerificationAddress extends Address {
  carrierRoute: string | null;
}
interface AddressVerificationOption {
  format: string;
  title: string;
  address: VerificationAddress;
}

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements OnInit {
  @Input() visible!: boolean;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() submitForm: EventEmitter<number> = new EventEmitter<number>();

  @Select(LookupState.usStates) usStates$!: Observable<UsState[]>;
  @Select(AddressesState.createAddressState) createAddressState$!: Observable<ApiStoreData<PatientAddress | null>>;

  newAddressForm = new FormGroup({
    line1: new FormControl(null, [Validators.required, Validators.maxLength(45)]),
    line2: new FormControl(null, [Validators.maxLength(45)]),
    attention: new FormControl(null, [Validators.maxLength(40)]),
    city: new FormControl(null, [Validators.required, Validators.maxLength(30)]),
    state: new FormControl(null, [Validators.required]),
    zipCode: new FormControl(null, [Validators.required, zipCodeValidator()]),
    types: new FormControl(null),
    addressVerified: new FormControl(false),
    carrierRoute: new FormControl(null),
  });

  initialValues: PatientAddressFormValues = {
    line1: null,
    line2: null,
    attention: null,
    city: null,
    state: null,
    zipCode: null,
    types: [SHIPPING_ADDRESS_TYPE, BILLING_ADDRESS_TYPE, CORRESPONDENCE_ADDRESS_TYPE],
    addressVerified: false,
    carrierRoute: null,
  };

  usStateOptions$ = this.usStates$.pipe(
    map((usStates) =>
      usStates.map((s) => ({
        label: s.name,
        value: s.value,
      }))
    )
  );

  addressVerificationForm: FormGroup = new FormGroup({
    selectedAddressFormat: new FormControl('usps'),
  });

  addressVerificationOptions: AddressVerificationOption[] = [];
  displayVerificationModal: boolean = false;
  isVerifyingAddress: boolean = false;
  isAddressVerificationAttempted: boolean = false;

  displayAddressOrdersModal: boolean = false;

  asFormControl = asFormControl;

  constructor(private store: Store, private addressVerificationService: AddressVerificationService) {}

  ngOnInit(): void {
    this.newAddressForm.setValue(omit(this.initialValues, 'id'));
    this.updateVerificationStateWithAddressChanges();
  }

  updateVerificationStateWithAddressChanges() {
    const addressChanges = combineLatest([
      this.newAddressForm.get('line1')?.valueChanges,
      this.newAddressForm.get('line2')?.valueChanges,
      this.newAddressForm.get('attention')?.valueChanges,
      this.newAddressForm.get('city')?.valueChanges,
      this.newAddressForm.get('state')?.valueChanges,
      this.newAddressForm.get('zipCode')?.valueChanges,
      this.newAddressForm.get('types')?.valueChanges,
    ]);

    addressChanges.subscribe(() => {
      this.isAddressVerificationAttempted = false;
      this.newAddressForm.get('addressVerified')?.setValue(false, { emitEvent: false });
    });
  }

  getNewAddressTypeOptions() {
    return ADDRESS_TYPE_OPTIONS;
  }

  onVisibleChange(value: boolean) {
    this.visibleChange.emit(value);
  }

  hideNewAddressModal() {
    this.visible = false;
    this.onVisibleChange(this.visible);
  }

  showNewAddressModal() {
    this.visible = true;
    this.onVisibleChange(this.visible);
  }

  showVerificationModal() {
    this.displayVerificationModal = true;
  }

  hideVerificationModal() {
    this.displayVerificationModal = false;
  }

  showAddressOrdersModal() {
    this.displayAddressOrdersModal = true;
  }

  hideAddressOrdersModal() {
    this.displayAddressOrdersModal = false;
  }

  changeSelectedAddressFormat(format: string) {
    this.addressVerificationForm?.get('selectedAddressFormat')?.setValue(format);
  }

  onCancelNewAddressForm() {
    this.hideNewAddressModal();
    this.newAddressForm.reset();
    this.newAddressForm.setValue(this.initialValues);
  }

  onSubmitNewAddressForm() {
    if (this.newAddressForm.valid) {
      if (this.isAddressVerificationAttempted) {
        this.submitNewAddressForm();
        return;
      }
      this.startVerification();
    } else {
      validateAllFormFields(this.newAddressForm);
    }
  }

  submitNewAddressForm() {
    this.store
      .dispatch(new CreatePatientAddress(this.newAddressForm.value))
      .pipe(withLatestFrom(this.createAddressState$))
      .subscribe(([_, createAddress]) => {
        if (createAddress.error) {
          this.hideVerificationModal();
          this.showNewAddressModal();
          return;
        } else {
          this.submitForm.emit(createAddress.data?.id || -1);
          this.hideVerificationModal();
          this.hideNewAddressModal();
          this.newAddressForm.reset();
          this.newAddressForm.setValue(this.initialValues);
        }
      });
  }

  onCancelAddressVerificationForm() {
    this.showNewAddressModal();
  }

  onSubmitAddressVerificationForm() {
    if (this.addressVerificationForm.get('selectedAddressFormat')!.value === 'usps') {
      const uspsOption = this.addressVerificationOptions.find((o) => o.format === 'usps');

      if (uspsOption) {
        this.newAddressForm.patchValue({
          line1: uspsOption.address.line1,
          line2: uspsOption.address.line2,
          city: uspsOption.address.city,
          state: uspsOption.address.state,
          zipCode: uspsOption.address.zipCode,
          attention: uspsOption.address.attention,
          addressVerified: true,
          carrierRoute: uspsOption.address.carrierRoute,
        });

        this.isAddressVerificationAttempted = true; // must set this flag after form changes
      }
    }

    this.submitNewAddressForm();
  }

  startVerification() {
    this.isVerifyingAddress = true;
    this.addressVerificationService.verifyPatientAddress(this.newAddressForm.value).subscribe(
      (v) => {
        parseXmlString(v, (error, result) => {
          const uspsAddress = result.AddressValidateResponse.Address[0];

          if (!uspsAddress || uspsAddress.Error?.length) {
            this.onVerificationFailure();
            return;
          }

          let zipCode = uspsAddress.Zip5[0];

          if (uspsAddress.Zip4[0].length > 0) {
            zipCode = zipCode + '-' + uspsAddress.Zip4[0];
          }

          this.addressVerificationOptions = [
            {
              format: 'usps',
              title: 'USPS Recommended Address',
              address: {
                ...mapAddress(
                  0,
                  uspsAddress.Address2[0],
                  uspsAddress.Address1 ? uspsAddress.Address1[0] : null,
                  uspsAddress.City[0],
                  uspsAddress.State[0],
                  zipCode,
                  this.newAddressForm.value.attention?.toUpperCase()
                )!,
                carrierRoute: uspsAddress.CarrierRoute ? uspsAddress.CarrierRoute[0] : null,
              },
            },
            {
              format: 'original',
              title: 'Original Address',
              address: mapPatientAddressFormValues(this.newAddressForm.value),
            },
          ];

          this.addressVerificationForm = new FormGroup({
            selectedAddressFormat: new FormControl('usps'),
          });

          this.onVerificationSuccess();
        });
      },
      (error) => {
        this.onVerificationFailure();
      }
    );
  }

  onVerificationFailure() {
    window.setTimeout(() => {
      this.isVerifyingAddress = false;
      this.isAddressVerificationAttempted = true;
      this.submitNewAddressForm();
    }, 1000);
  }

  onVerificationSuccess() {
    window.setTimeout(() => {
      this.isVerifyingAddress = false;
      this.isAddressVerificationAttempted = true;
      this.hideNewAddressModal();
      this.showVerificationModal();
    }, 1000);
  }
}
